/**
 * The single acknowledgement gate before a generated Progress Note opens —
 * the PD-1110 AI disclaimer plus any HIGH-severity quality flags, acknowledged
 * together. Non-dismissible: confirming is the only way past it.
 */
import { Box, Group, Stack, Text, ThemeIcon } from '@mantine/core'
import { IconAlertTriangle, IconSparkles } from '@tabler/icons-react'
import { modals } from '@mantine/modals'
import type { GenerateDraftResponse } from './useFinalizeScribe'

type DraftVerificationIssue = NonNullable<GenerateDraftResponse['verification']>['issues'][number]

/** `issues` are the judge's findings; only HIGH severity is surfaced (empty = disclaimer only). */
export function confirmAiNote(issues: DraftVerificationIssue[] = []): Promise<void> {
  const high = issues.filter((issue) => issue.severity === 'high')
  return new Promise((resolve) => {
    modals.openConfirmModal({
      title: (
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon size={32} radius="xl" variant="light" color="yellow">
            <IconSparkles size={17} />
          </ThemeIcon>
          <Text fw={700} size="md">
            Your progress note is ready
          </Text>
        </Group>
      ),
      centered: true,
      radius: 'md',
      size: 'md',
      padding: 'lg',
      withCloseButton: false,
      closeOnClickOutside: false,
      closeOnEscape: false,
      transitionProps: { transition: 'pop', duration: 220 },
      children: (
        <Stack gap="md" pt={4}>
          <Text size="sm" c="dimmed" lh={1.55}>
            AI drafted this note from the visit conversation. Review every section — verify the
            diagnoses, medications, and orders — and edit as needed. You are responsible for the
            final content.
          </Text>
          {high.length > 0 ? (
            <Stack gap="xs" mah={280} style={{ overflowY: 'auto' }}>
              <Group gap={6} wrap="nowrap">
                <IconAlertTriangle size={15} color="var(--mantine-color-orange-6)" />
                <Text size="sm" fw={600}>
                  {high.length} item{high.length === 1 ? '' : 's'} to check before signing
                </Text>
              </Group>
              {high.map((issue, index) => (
                <Box
                  key={index}
                  p="sm"
                  style={{
                    borderRadius: 8,
                    background: 'var(--mantine-color-orange-0)',
                    borderLeft: '3px solid var(--mantine-color-orange-4)',
                  }}
                >
                  <Text size="xs" fw={700} tt="uppercase" c="orange.8" mb={4}>
                    {issue.section}
                  </Text>
                  <Text size="sm" lh={1.5}>
                    {issue.description}
                  </Text>
                  {issue.evidence.trim() ? (
                    <Text size="xs" c="dimmed" mt={6} fs="italic" lh={1.45}>
                      “{issue.evidence}”
                    </Text>
                  ) : null}
                </Box>
              ))}
            </Stack>
          ) : null}
        </Stack>
      ),
      labels: {
        confirm: high.length > 0 ? "I've reviewed these — open the note" : 'Open the note',
        cancel: '',
      },
      confirmProps: { size: 'md', radius: 'md' },
      groupProps: { grow: true },
      cancelProps: { display: 'none' },
      onConfirm: () => resolve(),
      // The modal has no user-facing dismiss, so onClose only fires on a
      // programmatic close (e.g. session-timeout logout) — resolve so the flow
      // never strands rather than hanging the awaited promise forever.
      onClose: () => resolve(),
    })
  })
}
